// src/components/account/AccountSettingsDialog.tsx

import { useState, useEffect, useRef } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';

import {
  EmailAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from 'contexts/FirebaseContext';

import useAuth from 'hooks/useAuth';
import { openSnackbar } from 'api/snackbar';

// Icons
import {
  Eye,
  EyeClosed,
  UserCrown,
  UserStar,
  Building,
  ShopFourTiles,
  ShieldCheck,
  ShieldXmark,
  Key,
  Mail,
  Hashtag,
} from 'iconoir-react';

// ---------- Role Config ----------

const ROLE_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  superadmin: { icon: UserCrown, label: 'Super Admin', color: 'error' },
  admin: { icon: UserStar, label: 'Admin', color: 'warning' },
  wholesale: { icon: Building, label: 'Wholesale', color: 'info' },
  reseller: { icon: ShopFourTiles, label: 'Reseller', color: 'success' },
};

// ---------- Types ----------

interface AccountSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

type MfaStep = 'idle' | 'enterPhone' | 'enterCode' | 'processing';
type DisableStep = 'password' | 'smsCode' | 'processing';

// ---------- Component ----------

export default function AccountSettingsDialog({ open, onClose }: AccountSettingsDialogProps) {
  const { resetPassword, startMfaEnrollment, finishMfaEnrollment } = useAuth();

  // User state from Firebase
  const [userEmail, setUserEmail] = useState<string>('');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [resellerId, setResellerId] = useState<string | number | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaPhoneNumber, setMfaPhoneNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // MFA enrollment state
  const [mfaStep, setMfaStep] = useState<MfaStep>('idle');
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  // MFA disable dialog state
  const [disableMfaDialogOpen, setDisableMfaDialogOpen] = useState(false);
  const [disableStep, setDisableStep] = useState<DisableStep>('password');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableSmsCode, setDisableSmsCode] = useState('');
  const [disableError, setDisableError] = useState<string | null>(null);
  const [showDisablePassword, setShowDisablePassword] = useState(false);

  // MFA resolver for disable flow
  const [disableMfaResolver, setDisableMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [disableVerificationId, setDisableVerificationId] = useState<string | null>(null);
  const disableRecaptchaRef = useRef<RecaptchaVerifier | null>(null);

  // Password reset state
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  // Load user data when dialog opens
  useEffect(() => {
    if (open) {
      loadUserData();
    }
  }, [open]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      await user.reload();
      const token = await user.getIdTokenResult(true);

      setUserEmail(user.email || '');
      setUserRole(token.claims.role as string || null);
      setResellerId(token.claims.reseller_id as string | number || null);

      // Check MFA status
      const enrolledFactors = multiFactor(user).enrolledFactors || [];
      setMfaEnabled(enrolledFactors.length > 0);

      if (enrolledFactors.length > 0) {
        const phoneFactor = enrolledFactors.find(f => f.factorId === 'phone');
        if (phoneFactor && 'phoneNumber' in phoneFactor) {
          setMfaPhoneNumber((phoneFactor as any).phoneNumber || null);
        }
      }
    } catch (err) {
      console.error('Failed to load user data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleChangePassword = async () => {
    if (!userEmail) return;

    setSendingReset(true);
    try {
      await resetPassword?.(userEmail);
      openSnackbar({
        open: true,
        message: `Password reset email sent to ${userEmail}`,
        variant: 'alert',
        alert: { color: 'success' }
      });
    } catch (err) {
      console.error('Password reset failed:', err);
      openSnackbar({
        open: true,
        message: 'Failed to send password reset email',
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setSendingReset(false);
    }
  };

  // Handle MFA toggle
  const handleMfaToggle = () => {
    if (mfaEnabled) {
      // Disable MFA - show confirmation dialog
      setDisableMfaDialogOpen(true);
      setDisableStep('password');
      setDisablePassword('');
      setDisableSmsCode('');
      setDisableError(null);
    } else {
      // Enable MFA - start enrollment flow
      setMfaStep('enterPhone');
      setMfaError(null);
    }
  };

  // Start MFA enrollment
  const handleStartMfaEnrollment = async () => {
    if (!phoneInput) {
      setMfaError('Please enter a phone number');
      return;
    }

    let formattedPhone = phoneInput.trim();
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+' + formattedPhone;
    }

    setMfaStep('processing');
    setMfaError(null);

    try {
      await startMfaEnrollment?.(formattedPhone);
      setMfaStep('enterCode');
    } catch (err: any) {
      console.error('MFA enrollment start failed:', err);
      setMfaError(err.message || 'Failed to send verification code');
      setMfaStep('enterPhone');
    }
  };

  // Complete MFA enrollment
  const handleFinishMfaEnrollment = async () => {
    if (!codeInput || codeInput.length !== 6) {
      setMfaError('Please enter the 6-digit code');
      return;
    }

    setMfaStep('processing');
    setMfaError(null);

    try {
      await finishMfaEnrollment?.(codeInput);
      setMfaEnabled(true);
      setMfaStep('idle');
      setPhoneInput('');
      setCodeInput('');
      openSnackbar({
        open: true,
        message: '2FA enabled successfully',
        variant: 'alert',
        alert: { color: 'success' }
      });
      loadUserData();
    } catch (err: any) {
      console.error('MFA enrollment finish failed:', err);
      setMfaError(err.message || 'Invalid verification code');
      setMfaStep('enterCode');
    }
  };

  // Get or create RecaptchaVerifier for disable flow
  const getDisableRecaptchaVerifier = () => {
    if (!disableRecaptchaRef.current) {
      disableRecaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-disable-container', {
        size: 'invisible'
      });
    }
    return disableRecaptchaRef.current;
  };

  // Step 1: Enter password to start re-authentication
  const handleDisablePasswordSubmit = async () => {
    const user = auth.currentUser;
    if (!user || !user.email) return;

    if (!disablePassword) {
      setDisableError('Please enter your password');
      return;
    }

    setDisableStep('processing');
    setDisableError(null);

    try {
      // Try to re-authenticate - this will trigger MFA challenge
      const credential = EmailAuthProvider.credential(user.email, disablePassword);
      await reauthenticateWithCredential(user, credential);

      // If we get here without MFA error, user doesn't have MFA (shouldn't happen)
      await unenrollMfa();
    } catch (err: any) {
      console.error('Re-auth error:', err);

      if (err.code === 'auth/multi-factor-auth-required') {
        // MFA required - send SMS code
        // Modular API: derive the resolver from the error.
        const resolver = getMultiFactorResolver(auth, err);
        setDisableMfaResolver(resolver);

        try {
          const recaptchaVerifier = getDisableRecaptchaVerifier();
          const phoneInfoOptions = {
            multiFactorHint: resolver.hints[0],
            session: resolver.session
          };

          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            recaptchaVerifier
          );

          setDisableVerificationId(verificationId);
          setDisableStep('smsCode');
        } catch (smsErr: any) {
          console.error('SMS send error:', smsErr);
          setDisableError(smsErr.message || 'Failed to send SMS code');
          setDisableStep('password');
        }
      } else if (err.code === 'auth/wrong-password') {
        setDisableError('Incorrect password');
        setDisableStep('password');
      } else if (err.code === 'auth/too-many-requests') {
        setDisableError('Too many attempts. Please try again later.');
        setDisableStep('password');
      } else {
        setDisableError(err.message || 'Authentication failed');
        setDisableStep('password');
      }
    }
  };

  // Step 2: Verify SMS code and complete re-authentication
  const handleDisableSmsSubmit = async () => {
    if (!disableMfaResolver || !disableVerificationId) {
      setDisableError('Session expired. Please try again.');
      setDisableStep('password');
      return;
    }

    if (!disableSmsCode || disableSmsCode.length !== 6) {
      setDisableError('Please enter the 6-digit code');
      return;
    }

    setDisableStep('processing');
    setDisableError(null);

    try {
      // Complete MFA sign-in
      const cred = PhoneAuthProvider.credential(disableVerificationId, disableSmsCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      await disableMfaResolver.resolveSignIn(multiFactorAssertion);

      // Now unenroll MFA
      await unenrollMfa();
    } catch (err: any) {
      console.error('SMS verify error:', err);
      
      if (err.code === 'auth/invalid-verification-code') {
        setDisableError('Invalid verification code');
      } else {
        setDisableError(err.message || 'Verification failed');
      }
      setDisableStep('smsCode');
    }
  };

  // Actually unenroll MFA factors
  const unenrollMfa = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const enrolledFactors = multiFactor(user).enrolledFactors || [];
      for (const factor of enrolledFactors) {
        await multiFactor(user).unenroll(factor);
      }

      setMfaEnabled(false);
      setMfaPhoneNumber(null);
      handleCloseDisableDialog();

      openSnackbar({
        open: true,
        message: '2FA disabled successfully',
        variant: 'alert',
        alert: { color: 'warning' }
      });
    } catch (err: any) {
      console.error('Unenroll error:', err);
      setDisableError(err.message || 'Failed to disable 2FA');
      setDisableStep('password');
    }
  };

  // Cancel MFA enrollment
  const handleCancelMfaEnrollment = () => {
    setMfaStep('idle');
    setPhoneInput('');
    setCodeInput('');
    setMfaError(null);
  };

  // Close disable dialog
  const handleCloseDisableDialog = () => {
    setDisableMfaDialogOpen(false);
    setDisableStep('password');
    setDisablePassword('');
    setDisableSmsCode('');
    setDisableError(null);
    setDisableMfaResolver(null);
    setDisableVerificationId(null);
    
    // Clear recaptcha
    if (disableRecaptchaRef.current) {
      try {
        disableRecaptchaRef.current.clear();
      } catch {}
      disableRecaptchaRef.current = null;
    }
  };

  // Close main dialog
  const handleClose = () => {
    handleCancelMfaEnrollment();
    onClose();
  };

  const roleConfig = userRole ? ROLE_CONFIG[userRole] : null;
  const RoleIcon = roleConfig?.icon;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Account Settings</DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* User Info Section */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Account Information
                </Typography>
                
                <Stack spacing={2}>
                  {/* Email */}
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Mail style={{ fontSize: 20, color: '#666' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">{userEmail}</Typography>
                    </Box>
                  </Stack>

                  {/* Role */}
                  {roleConfig && RoleIcon && (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <RoleIcon style={{ fontSize: 20, color: '#666' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Role
                        </Typography>
                        <Typography variant="body1">{roleConfig.label}</Typography>
                      </Box>
                    </Stack>
                  )}

                  {/* Reseller ID */}
                  {resellerId && (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Hashtag style={{ fontSize: 20, color: '#666' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          ID
                        </Typography>
                        <Typography variant="body1">{resellerId}</Typography>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Security Section */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Security
                </Typography>

                <Stack spacing={2}>
                  {/* Change Password */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Key style={{ fontSize: 20, color: '#666' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Password</Typography>
                        <Typography variant="body1">
                          Change your account password
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setChangePasswordDialogOpen(true)}
                      disabled={sendingReset}
                    >
                      {sendingReset ? 'Sending...' : 'Change'}
                    </Button>
                  </Stack>

                  <Divider />

                  {/* 2FA / MFA */}
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        {mfaEnabled ? (
                          <ShieldCheck style={{ fontSize: 20, color: 'green' }} />
                        ) : (
                          <ShieldXmark style={{ fontSize: 20, color: '#666' }} />
                        )}
                        <Box>
                          <Typography variant="body2" color="text.secondary">Two-Factor Authentication</Typography>
                          <Typography variant="body1">
                            {mfaEnabled
                              ? `Enabled${mfaPhoneNumber ? ` (${mfaPhoneNumber})` : ''}`
                              : 'Add an extra layer of security'}
                          </Typography>                          
                        </Box>
                      </Stack>

                      {mfaStep === 'idle' && (
                        <Switch
                          checked={mfaEnabled}
                          onChange={handleMfaToggle}
                          color="success"
                        />
                      )}

                      {mfaStep === 'processing' && <CircularProgress size={24} />}
                    </Stack>

                    {/* MFA Enrollment Flow */}
                    {mfaStep === 'enterPhone' && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" gutterBottom>
                          Enter your phone number to receive verification codes:
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <TextField
                            size="small"
                            placeholder="+32 470 123 456"
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(e.target.value)}
                            fullWidth
                          />
                          <Button variant="contained" onClick={handleStartMfaEnrollment}>
                            Send
                          </Button>
                          <Button variant="outlined" onClick={handleCancelMfaEnrollment}>
                            Cancel
                          </Button>
                        </Stack>
                        {mfaError && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {mfaError}
                          </Alert>
                        )}
                      </Box>
                    )}

                    {mfaStep === 'enterCode' && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" gutterBottom>
                          Enter the 6-digit code sent to your phone:
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <TextField
                            size="small"
                            placeholder="123456"
                            value={codeInput}
                            onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            fullWidth
                            inputProps={{ maxLength: 6 }}
                          />
                          <Button variant="contained" onClick={handleFinishMfaEnrollment}>
                            Verify
                          </Button>
                          <Button variant="outlined" onClick={handleCancelMfaEnrollment}>
                            Cancel
                          </Button>
                        </Stack>
                        {mfaError && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {mfaError}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invisible reCAPTCHA containers */}
      <div id="recaptcha-enroll-container" />
      <div id="recaptcha-disable-container" />

      {/* Disable 2FA Dialog - Multi-step */}
      <Dialog 
        open={disableMfaDialogOpen} 
        onClose={handleCloseDisableDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Step 1: Password */}
            {disableStep === 'password' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  To disable 2FA, please enter your password to confirm your identity.
                </Typography>
                
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="disable-password">Password</InputLabel>
                  <OutlinedInput
                    id="disable-password"
                    fullWidth
                    type={showDisablePassword ? 'text' : 'password'}
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    error={!!disableError}
                    placeholder="Enter your password"
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowDisablePassword(!showDisablePassword)}
                          edge="end"
                          size="small"
                        >
                          {showDisablePassword ? <Eye /> : <EyeClosed />}
                        </IconButton>
                      </InputAdornment>
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleDisablePasswordSubmit();
                      }
                    }}
                  />
                  {disableError && (
                    <FormHelperText error>{disableError}</FormHelperText>
                  )}
                </Stack>

                <Alert severity="warning" variant="outlined">
                  This will remove the extra layer of security from your account.
                </Alert>
              </>
            )}

            {/* Step 2: SMS Code */}
            {disableStep === 'smsCode' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  We've sent a verification code to your phone. Enter it below to confirm.
                </Typography>
                
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="disable-sms-code">Verification Code</InputLabel>
                  <OutlinedInput
                    id="disable-sms-code"
                    fullWidth
                    value={disableSmsCode}
                    onChange={(e) => setDisableSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    error={!!disableError}
                    placeholder="123456"
                    inputProps={{ maxLength: 6 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleDisableSmsSubmit();
                      }
                    }}
                  />
                  {disableError && (
                    <FormHelperText error>{disableError}</FormHelperText>
                  )}
                </Stack>
              </>
            )}

            {/* Processing */}
            {disableStep === 'processing' && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDisableDialog} 
            disabled={disableStep === 'processing'}
          >
            Cancel
          </Button>
          
          {disableStep === 'password' && (
            <Button 
              onClick={handleDisablePasswordSubmit} 
              color="error" 
              variant="contained"
              disabled={!disablePassword}
            >
              Continue
            </Button>
          )}
          
          {disableStep === 'smsCode' && (
            <Button 
              onClick={handleDisableSmsSubmit} 
              color="error" 
              variant="contained"
              disabled={!disableSmsCode || disableSmsCode.length !== 6}
            >
              Disable 2FA
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Change Password Confirmation Dialog */}
      <Dialog 
        open={changePasswordDialogOpen} 
        onClose={() => setChangePasswordDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We will send a password reset link to <strong>{userEmail}</strong>. 
            Click the link in the email to set a new password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              setChangePasswordDialogOpen(false);
              await handleChangePassword();
            }} 
            variant="contained"
            disabled={sendingReset}
          >
            {sendingReset ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
